import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { AiKnowledgeConfig, RouteCommand } from './interface/config.interface';
import { AiKnowledgeService } from './ai-knowledge.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  AI_KNOWLEDGE_CONFIG_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
  WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
} from './tokens';
import { isObservable, Observable, of } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { KnowledgeIcon } from './assets/avatar-svg-string';

@NgModule()
export class AiKnowledgeModule {
  static forRoot(c: { config: InjectionToken<AiKnowledgeConfig> }): ModuleWithProviders<AiKnowledgeModule> {
    const result = {
      ngModule: AiKnowledgeModule,
      providers: [
        AiKnowledgeService,
        { provide: AI_KNOWLEDGE_CONFIG_TOKEN, useExisting: c.config },
        { provide: ACCOUNT_GROUP_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: accountGroupIdFactory },
        { provide: MARKET_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: marketIdFactory },
        { provide: PARTNER_ID_TOKEN, deps: [AI_KNOWLEDGE_CONFIG_TOKEN], useFactory: partnerIdFactory },
        {
          provide: MANAGE_KNOWLEDGE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: manageKnowledgeUrlFactory,
        },
        {
          provide: BUSINESS_PROFILE_URL_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: businessProfileUrlFactory,
        },
        {
          provide: SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: showBusinessProfileSourceFactory,
        },
        {
          provide: WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
          deps: [AI_KNOWLEDGE_CONFIG_TOKEN],
          useFactory: webChatWidgetEditRouteFactory,
        },
      ],
    };
    return result;
  }

  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIconLiteral('ai-knowledge-icon', sanitizer.bypassSecurityTrustHtml(KnowledgeIcon));
  }
}

function accountGroupIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.accountGroupId$) ? config.accountGroupId$ : of(config.accountGroupId$);
}

function marketIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.marketId$) ? config.marketId$ : of(config.marketId$);
}

function partnerIdFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.partnerId$) ? config.partnerId$ : of(config.partnerId$);
}

function manageKnowledgeUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.manageKnowledgeUrl$) ? config.manageKnowledgeUrl$ : of(config.manageKnowledgeUrl$);
}

function businessProfileUrlFactory(config: AiKnowledgeConfig): Observable<string | undefined | null> {
  return isObservable(config.businessProfileUrl$) ? config.businessProfileUrl$ : of(config.businessProfileUrl$);
}

function showBusinessProfileSourceFactory(config: AiKnowledgeConfig): Observable<boolean | undefined | null> {
  return isObservable(config.showBusinessProfileSource$)
    ? config.showBusinessProfileSource$
    : of(config.showBusinessProfileSource$);
}

function webChatWidgetEditRouteFactory(config: AiKnowledgeConfig): Observable<RouteCommand[] | undefined | null> {
  return isObservable(config.webChatWidgetEditRoute$)
    ? config.webChatWidgetEditRoute$
    : of(config.webChatWidgetEditRoute$);
}
